<template>
    <DsListItem
        border-style="full"
    >
        <template #title>
            <DsStatus emphasis="high" :type="itemType">
                {{ $t(statusKey) }}
            </DsStatus>

            <div class="title">
                {{ domain.domain }}
            </div>
        </template>

        <template #description>
            <div>
                {{ $t(`email.auth.listItem.description.${status}`, { domain: domain.domain }) }}
            </div>
        </template>

        <template #trailing>
            <template>
                <DsOutlineButton v-if="showConnectButton" class="connect-button" @click="$emit(connectButtonEvent, domain)">
                    <template #leading>
                        <DsIcon :name="connectButtonIcon()" />
                    </template>

                    <template #default>
                        {{ $t(`email.auth.listItem.connectButton.${status}`) }}
                    </template>
                </DsOutlineButton>

                <div v-else class="action-container">
                    <template v-if="!isEmailDomainAuthStatusEnabled">
                        <template v-if="showRefreshAction">
                            <div v-if="isRefreshing" class="refresh-spinner-container">
                                <DsSpinner :size="24" />
                            </div>

                            <DsIconButton
                                v-else
                                name="refresh-cw"
                                :aria-label="$t('global.refresh')"
                                @click="refresh"
                            />
                        </template>

                        <DsIconButton
                            v-if="showEditAction"
                            name="edit"
                            :aria-label="$t('global.edit')"
                            @click="$emit('edit-email-auth', domain)"
                        />

                        <DsIconButton
                            v-if="showInfoAction"
                            name="info-circle"
                            :aria-label="$t('global.edit')"
                            @click="$emit('edit-email-auth', domain)"
                        />
                    </template>

                    <DsIconButton
                        v-else-if="showPendingAction"
                        name="info-circle"
                        :aria-label="$t('global.status')"
                        @click="$emit('show-status-email-auth', domain)"
                    />

                    <DsIconButton
                        v-else-if="showInfoAction"
                        name="info-circle"
                        :aria-label="$t('global.status')"
                        @click="$emit('show-status-email-auth', domain)"
                    />

                    <DsIconButton name="x" :aria-label="$t('global.remove')" @click="openDeleteConfirmDialog" />

                    <DsConfirmDialog
                        class="domain-delete-confirm"
                        :is-open="confirmDeleteOpen"
                        destructive
                        :title="$t('email.auth.listItem.deleteConfirmTitle')"
                        :message="$t('email.auth.listItem.deleteConfirmMessage')"
                        :confirm-label="$t('email.auth.listItem.deleteConfirmLabel')"
                        :cancel-label="$t('email.auth.listItem.deleteCancelLabel')"
                        @cancel="closeDeleteConfirm"
                        @confirm="deleteDomain"
                    />
                </div>
            </template>
        </template>
    </DsListItem>
</template>

<script>
import axios from 'axios';

const ITEM_TYPE_AVAILABLE = 'default';
const ITEM_TYPE_CONNECTED = 'good';
const ITEM_TYPE_EXPIRED = 'error';
const ITEM_TYPE_PENDING = 'warning';

export default {
    props: {
        domain: {
            type: Object,
        },
        status: {
            type: String,
        },
        isEmailDomainAuthStatusEnabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'refresh-email-auth',
        'edit-email-auth',
        'show-status-email-auth',
        'delete-email-auth',
        'add-email-auth',
    ],

    data() {
        return {
            isRefreshing: false,
            confirmDeleteOpen: false,
        };
    },

    computed: {
        itemType() {
            switch (this.status) {
            case 'issue':
                return ITEM_TYPE_EXPIRED;
            case 'suggested':
                return ITEM_TYPE_AVAILABLE;
            case 'pending':
                return ITEM_TYPE_PENDING;
            case 'connected':
                return ITEM_TYPE_CONNECTED;
            default:
                return ITEM_TYPE_PENDING;
            }
        },

        showConnectButton() {
            if (this.itemType === ITEM_TYPE_AVAILABLE || this.itemType === ITEM_TYPE_EXPIRED) {
                return true;
            }

            return false;
        },

        showRefreshAction() {
            return this.itemType === ITEM_TYPE_PENDING;
        },

        showPendingAction() {
            return this.itemType === ITEM_TYPE_PENDING;
        },

        showEditAction() {
            return this.itemType === ITEM_TYPE_PENDING;
        },

        showInfoAction() {
            return this.itemType === ITEM_TYPE_CONNECTED;
        },

        connectButtonEvent() {
            return this.itemType === ITEM_TYPE_EXPIRED ? 'edit-email-auth' : 'add-email-auth';
        },

        statusKey() {
            return `email.auth.listItem.status.${this.status}`;
        },
    },
    methods: {
        async openDeleteConfirmDialog() {
            this.confirmDeleteOpen = true;
        },

        closeDeleteConfirm() {
            this.confirmDeleteOpen = false;
        },

        deleteDomain() {
            this.$emit('delete-email-auth', this.domain);
            this.confirmDeleteOpen = false;
        },

        connectButtonIcon() {
            return this.itemType === ITEM_TYPE_AVAILABLE ? 'add' : 'refresh-cw';
        },

        async refresh() {
            this.isRefreshing = true;

            try {
                await axios.post(`/app/emailAuthorization/${this.domain.domain}/retryDomainVerification`);

                this.isRefreshing = false;
                this.$emit('refresh-email-auth');
            } catch (_) {
                this.isRefreshing = false;
                this.$error({
                    message: this.$t('email.auth.domainRefreshError'),
                });
            }
        },
    },
};
</script>


<style lang="scss" scoped>
    .domain-delete-confirm.modal-overlay.confirm-modal ::v-deep {
        width: auto;
        height: auto;

        .modal-content-wrapper.dialog {
            width: auto;
            height: auto;
        }
    }

    .domain-delete-confirm ::v-deep {
        h4.title {
            font-size: $font-size-lg;
        }
        .modal-body-inner {
            padding: $spacing-100;
        }
        button.gray {
            color: $color-ink-900;
        }
        button.confirm-button {
            color: $color-red-800;
        }
}
.connect-button {
    color: $color-blue-700;
    white-space: nowrap;
    .leading .icon {
        fill: $color-blue-700;
    }
}

.item-action {
    color: $color-ink-900;
    cursor: pointer;
}

.refresh-spinner {
    margin: $spacing-100;
}

.refresh-spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: $spacing-100;
}

.action-container {
    display: flex;
    .item-action {
        --icon-color: black;
    }
}

.title {
    margin-top: $spacing-050;
}

</style>
